/**
 * A DÓNDE SE DEPOSITA. La misma caja para el club y para cada torneo.
 *
 * QUÉ PROBLEMA RESUELVE. La caja del comprobante pedía la foto de un pago sin
 * decir cuánto había que pagar ni a qué cuenta: el número andaba dando vueltas
 * por el grupo de WhatsApp del club, y quien se anotaba de noche no tenía
 * dónde buscarlo. Javier: «pon el monto que debo pagar y la información de las
 * cuentas a depositar».
 *
 * DOS DUEÑOS Y UNA SOLA CAJA. El club guarda las suyas una vez —son el molde—
 * y cada torneo nuevo arranca con una copia que después es suya. Son la misma
 * cuenta en dos momentos, y por eso esto recibe la lista y dos funciones en
 * vez de un torneo: una segunda caja igual sería la que un día pierde el
 * arreglo que se le hizo a la otra.
 *
 * VARIAS Y NO UNA. Los clubes tienen dos o tres —el banco, SINPE, a veces otra
 * de otro banco— y metidas todas en un campo de texto largo nadie sabe cuál
 * copiar. Cada una es una fila: se copia sola y se quita sola.
 *
 * SE AGREGAN DE A UNA Y NO SE EDITAN. Un número de cuenta mal escrito no se
 * corrige, se borra y se pone bien: editar en el lugar invita a dejar a medias
 * un número que la gente va a copiar para mandar plata.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACCOUNT_LIMITS, type PaymentAccount } from '@tennisup/contracts';

import { Alert } from './Alert';
import { errorMessage } from '../lib/errorMessage';

export function AccountsBox({
  accounts,
  help,
  onAdd,
  onRemove,
}: {
  accounts: readonly PaymentAccount[];
  /** Qué son estas cuentas para quien las está mirando. */
  help: string;
  onAdd: (input: { number: string; description: string }) => Promise<void>;
  onRemove: (accountId: string) => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();

  /**
   * LA LISTA, DEFENDIDA. `PaymentAccount[]` es una promesa sobre el contrato,
   * no una garantía sobre el cable.
   *
   * Un servidor una versión atrás manda el club SIN `accounts` —la ficha
   * existía mucho antes que las cuentas— y `accounts.length` tira la pantalla
   * entera con un «Cannot read properties of undefined». Ya pasó exactamente
   * así con las entradas de la Actividad, y `?? []` no alcanza para todos los
   * casos: `Array.isArray` sí.
   */
  const cuentas = Array.isArray(accounts) ? accounts : [];

  const [number, setNumber] = useState('');
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listo = number.trim() !== '' && description.trim() !== '';

  async function agregar(): Promise<void> {
    if (!listo || sending) return;
    setSending(true);
    setError(null);
    try {
      await onAdd({ number: number.trim(), description: description.trim() });
      // Se vacían los dos: lo normal es agregar la siguiente cuenta, no
      // corregir la que se acaba de guardar.
      setNumber('');
      setDescription('');
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  async function quitar(accountId: string): Promise<void> {
    setError(null);
    try {
      await onRemove(accountId);
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  return (
    <section className="accounts-box">
      <div className="section-head">
        <h3>{t('entries.accounts')}</h3>
        <span className="tag">{cuentas.length}</span>
      </div>
      <p className="help">{help}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {/* CUANDO NO HAY, SE DICE. Antes la lista simplemente no aparecía, y
          «no hay ninguna» y «esto no cargó» se ven exactamente igual. */}
      {cuentas.length === 0 ? (
        <p className="help">{t('tournaments.noAccounts')}</p>
      ) : (
        <ul className="member-list">
          {cuentas.map((cuenta) => (
            <li key={cuenta.id}>
              <div className="member-name">
                <strong className="proof-account-number">{cuenta.number}</strong>
                <span className="help">{cuenta.description}</span>
              </div>
              <button
                type="button"
                className="link danger"
                onClick={() => void quitar(cuenta.id)}
              >
                {t('common.remove')}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="field">
        <label htmlFor="account-number">{t('tournaments.accountNumber')}</label>
        <input
          id="account-number"
          type="text"
          autoComplete="off"
          maxLength={ACCOUNT_LIMITS.numberMax}
          placeholder={t('tournaments.accountNumberPlaceholder')}
          value={number}
          disabled={sending}
          onChange={(e) => setNumber(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="account-description">{t('tournaments.accountDescription')}</label>
        <input
          id="account-description"
          type="text"
          autoComplete="off"
          maxLength={ACCOUNT_LIMITS.descriptionMax}
          placeholder={t('tournaments.accountDescriptionPlaceholder')}
          value={description}
          disabled={sending}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <p className="button-row">
        <button type="button" disabled={!listo || sending} onClick={() => void agregar()}>
          {t('tournaments.addAccount')}
        </button>
      </p>
    </section>
  );
}
